/**
 * Helper functions for processing passage text and HTML
 * Used by ListeningTestUI and ListeningTestUI2
 */

const READING_HIGHLIGHT_CLASS = 'ielts-reading-note-highlight';
const HIGHLIGHT_TEXT_COLOR = '#2b2200';
const HIGHLIGHT_BACKGROUND = '#ffe98a';
const NOTE_UNDERLINE = '#d6a900';
const readingHighlightState = new Map();
let readingHighlightController = null;
let pendingReadingSelection = null;
let suppressReadingDocumentClicksUntil = 0;

/**
 * Find question number from previous siblings (including inside elements)
 */
export function findQuestionNumberFromSiblings(node) {
  while (node) {
    if (node.nodeType === 3) {
      const match = node.textContent.match(/<(\d+)>/);
      if (match) return match[1];
    } else if (node.nodeType === 1) {
      const match = node.textContent.match(/<(\d+)>/);
      if (match) return match[1];
    }
    node = node.previousSibling;
  }
  return null;
}

/**
 * Clean angle bracket markers from HTML (used for question-box content)
 */
function cleanAngleBrackets(html) {
  if (!html) return html;
  return html
    .replace(/(&lt;(\d+)&gt;)/g, '')
    .replace(/(<(\d+)>)/g, '');
}

function getReadingHighlightKey(container) {
  const passageText = getContainerText(container).replace(/\s+/g, ' ').trim();
  return passageText ? `ielts-reading:${passageText.slice(0, 120)}:${passageText.length}` : 'ielts-reading:empty';
}

function getReadingPassageContainer() {
  return document.querySelector('.ielts-passage-html');
}

function getTextNodes(root, options = {}) {
  const { includeHighlights = false } = options;
  const nodes = [];
  if (!root) return nodes;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      if (node.parentElement?.closest('.ielts-official-answer-label, .ielts-official-answer-info')) {
        return NodeFilter.FILTER_REJECT;
      }
      if (!includeHighlights && node.parentElement?.closest(`.${READING_HIGHLIGHT_CLASS}`)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  while (walker.nextNode()) {
    nodes.push(walker.currentNode);
  }
  return nodes;
}

function getContainerText(container) {
  return getTextNodes(container, { includeHighlights: true })
    .map((node) => node.nodeValue || '')
    .join('');
}

function getRangeOffsets(container, range) {
  const nodes = getTextNodes(container, { includeHighlights: true });
  let offset = 0;
  let start = null;
  let end = null;

  nodes.forEach((node) => {
    const length = node.nodeValue.length;
    if (node === range.startContainer) start = offset + range.startOffset;
    if (node === range.endContainer) end = offset + range.endOffset;
    offset += length;
  });

  if (start === null || end === null || end <= start) return null;
  return { start, end };
}

function findNodeAtOffset(nodes, targetOffset, bias = 'end') {
  let offset = 0;
  for (const node of nodes) {
    const nextOffset = offset + node.nodeValue.length;
    const isBoundary = targetOffset === offset;

    if (
      targetOffset < nextOffset
      || (targetOffset === nextOffset && bias === 'end')
      || (isBoundary && bias === 'start')
    ) {
      return { node, offset: Math.max(0, targetOffset - offset) };
    }
    offset = nextOffset;
  }
  const last = nodes[nodes.length - 1];
  return last ? { node: last, offset: last.nodeValue.length } : null;
}

function unwrapExistingHighlights(container) {
  container.querySelectorAll(`.${READING_HIGHLIGHT_CLASS}`).forEach((highlight) => {
    highlight.replaceWith(document.createTextNode(highlight.textContent || ''));
  });
  container.normalize();
}

function renderReadingHighlights(container, key, highlightsOverride = null) {
  const highlights = highlightsOverride || readingHighlightState.get(key) || [];
  unwrapExistingHighlights(container);

  [...highlights]
    .sort((a, b) => b.start - a.start)
    .forEach((highlight) => {
      const nodes = getTextNodes(container);
      const startPoint = findNodeAtOffset(nodes, highlight.start, 'start');
      const endPoint = findNodeAtOffset(nodes, highlight.end, 'end');
      if (!startPoint || !endPoint) return;

      const range = document.createRange();
      try {
        range.setStart(startPoint.node, startPoint.offset);
        range.setEnd(endPoint.node, endPoint.offset);
      } catch (error) {
        return;
      }

      const mark = document.createElement('mark');
      mark.className = READING_HIGHLIGHT_CLASS;
      mark.dataset.highlightId = highlight.sourceHighlightId || highlight.id;
      mark.dataset.highlightType = highlight.type || 'marker';
      mark.title = highlight.note ? `Note: ${highlight.note}` : 'Click for options';
      mark.style.backgroundColor = HIGHLIGHT_BACKGROUND;
      mark.style.color = HIGHLIGHT_TEXT_COLOR;
      mark.style.borderRadius = '3px';
      mark.style.padding = '0 2px';
      mark.style.cursor = 'pointer';
      mark.style.boxShadow = highlight.type === 'note' ? `inset 0 -2px 0 ${NOTE_UNDERLINE}` : 'none';

      try {
        range.surroundContents(mark);
      } catch (error) {
        const text = range.toString();
        mark.textContent = text;
        range.deleteContents();
        range.insertNode(mark);
      }
    });
}

function closeReadingFloatingControls() {
  const toolbar = document.getElementById('ielts-reading-selection-toolbar');
  const popover = document.getElementById('ielts-reading-note-popover');
  if (toolbar) toolbar.remove();
  if (popover) popover.remove();
}

function createFloatingShell(id, anchorRect, width = 260) {
  const shell = document.createElement('div');
  shell.id = id;
  shell.style.position = 'fixed';
  shell.style.zIndex = '10000';
  shell.style.width = `${width}px`;
  shell.style.padding = '10px';
  shell.style.border = '1px solid #d8d2c0';
  shell.style.borderRadius = '8px';
  shell.style.background = '#fffdf5';
  shell.style.boxShadow = '0 10px 30px rgba(0,0,0,0.16)';
  shell.style.fontFamily = 'inherit';

  const left = Math.min(anchorRect.left, window.innerWidth - width - 16);
  const top = anchorRect.bottom + 8 > window.innerHeight - 140 ? anchorRect.top - 120 : anchorRect.bottom + 8;
  shell.style.left = `${Math.max(8, left)}px`;
  shell.style.top = `${Math.max(8, top)}px`;

  shell.addEventListener('pointerdown', (event) => event.stopPropagation());
  shell.addEventListener('click', (event) => event.stopPropagation());

  return shell;
}

function createControlButton(label, title, variant = 'neutral') {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.title = title;
  button.style.border = variant === 'primary' ? 'none' : variant === 'danger' ? '1px solid #d9534f' : '1px solid #ddd';
  button.style.borderRadius = '6px';
  button.style.padding = '7px 10px';
  button.style.background = variant === 'primary' ? '#996CFE' : '#fff';
  button.style.color = variant === 'primary' ? '#fff' : variant === 'danger' ? '#d9534f' : '#333';
  button.style.cursor = 'pointer';
  button.style.fontSize = '14px';
  button.style.fontWeight = '600';
  return button;
}

function createToolbarIconButton(iconSvg, title, onPress) {
  const button = document.createElement('button');
  button.type = 'button';
  button.title = title;
  button.setAttribute('aria-label', title);
  button.innerHTML = iconSvg;
  button.style.width = '38px';
  button.style.height = '38px';
  button.style.border = 'none';
  button.style.borderRadius = '999px';
  button.style.background = '#996CFE';
  button.style.color = '#fff';
  button.style.cursor = 'pointer';
  button.style.display = 'inline-flex';
  button.style.alignItems = 'center';
  button.style.justifyContent = 'center';
  button.style.padding = '0';
  button.style.boxShadow = '0 4px 12px rgba(153,108,254,0.28)';

  button.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    suppressReadingDocumentClicksUntil = Date.now() + 600;
    onPress();
  });
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  return button;
}

function getMarkerIconSvg() {
  return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M4 17.5L14.8 6.7L17.3 9.2L6.5 20H4V17.5Z" fill="currentColor"/><path d="M16.2 5.3L17.6 3.9C18.1 3.4 18.9 3.4 19.4 3.9L20.1 4.6C20.6 5.1 20.6 5.9 20.1 6.4L18.7 7.8L16.2 5.3Z" fill="currentColor"/><path d="M3.5 21H12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
}

function getNoteIconSvg() {
  return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M6 3.8H15.5L20.2 8.5V20.2H6V3.8Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M15.3 4V8.7H20" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M8.8 12H17" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M8.8 15.5H15" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>';
}

function highlightsOverlap(first, second) {
  return Number(first.start) < Number(second.end) && Number(first.end) > Number(second.start);
}

function splitHighlightAroundRanges(highlight, blockedRanges, container) {
  const text = getContainerText(container);
  const segments = [{ start: highlight.start, end: highlight.end }];

  blockedRanges.forEach((range) => {
    for (let index = segments.length - 1; index >= 0; index -= 1) {
      const segment = segments[index];
      if (!highlightsOverlap(segment, range)) continue;

      const replacements = [];
      if (segment.start < range.start) {
        replacements.push({ start: segment.start, end: Math.min(segment.end, range.start) });
      }
      if (segment.end > range.end) {
        replacements.push({ start: Math.max(segment.start, range.end), end: segment.end });
      }
      segments.splice(index, 1, ...replacements);
    }
  });

  return segments
    .filter((segment) => segment.end > segment.start)
    .map((segment, index) => ({
      ...highlight,
      id: `${highlight.id}-visible-${index}`,
      sourceHighlightId: highlight.id,
      start: segment.start,
      end: segment.end,
      text: text.slice(segment.start, segment.end),
    }));
}

function getVisibleReadingHighlights(container, key, blockedRanges) {
  const highlights = readingHighlightState.get(key) || [];
  if (blockedRanges.length === 0) return highlights;

  return highlights.flatMap((highlight) => splitHighlightAroundRanges(highlight, blockedRanges, container));
}

function splitRangeByOverlap(range, overlapRanges) {
  const boundaries = new Set([range.start, range.end]);

  overlapRanges.forEach((overlapRange) => {
    if (!highlightsOverlap(range, overlapRange)) return;
    boundaries.add(Math.max(range.start, overlapRange.start));
    boundaries.add(Math.min(range.end, overlapRange.end));
  });

  const sorted = [...boundaries].sort((a, b) => a - b);
  const segments = [];

  for (let index = 0; index < sorted.length - 1; index += 1) {
    const start = sorted[index];
    const end = sorted[index + 1];
    if (end <= start) continue;
    segments.push({
      start,
      end,
      userOverlap: overlapRanges.some((overlapRange) => highlightsOverlap({ start, end }, overlapRange)),
    });
  }

  return segments;
}

function mergeReadingSelectionHighlights(existingHighlights, newHighlight, container) {
  const overlapping = [];
  const untouched = [];

  existingHighlights.forEach((highlight) => {
    if (highlightsOverlap(highlight, newHighlight)) {
      overlapping.push(highlight);
    } else {
      untouched.push(highlight);
    }
  });

  if (overlapping.length === 0) {
    return {
      highlights: [...existingHighlights, newHighlight].sort((a, b) => a.start - b.start),
      highlight: newHighlight,
    };
  }

  const noteHighlight = newHighlight.type === 'note'
    ? newHighlight
    : overlapping.find((highlight) => highlight.type === 'note' && highlight.note);
  const mergedHighlight = {
    ...newHighlight,
    id: newHighlight.type === 'note' ? newHighlight.id : (noteHighlight?.id || newHighlight.id),
    type: noteHighlight ? 'note' : newHighlight.type,
    note: noteHighlight?.note || '',
    start: Math.min(newHighlight.start, ...overlapping.map((highlight) => highlight.start)),
    end: Math.max(newHighlight.end, ...overlapping.map((highlight) => highlight.end)),
  };

  const text = getContainerText(container);
  mergedHighlight.text = text.slice(mergedHighlight.start, mergedHighlight.end);

  return {
    highlights: [...untouched, mergedHighlight].sort((a, b) => a.start - b.start),
    highlight: mergedHighlight,
  };
}

function saveReadingSelection(type) {
  const container = getReadingPassageContainer();
  if (!container || !pendingReadingSelection) return null;

  const currentKey = getReadingHighlightKey(container);
  const highlights = readingHighlightState.get(currentKey) || [];
  const highlight = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text: pendingReadingSelection.text,
    note: '',
    type,
    start: pendingReadingSelection.start,
    end: pendingReadingSelection.end,
  };

  const merged = mergeReadingSelectionHighlights(highlights, highlight, container);
  readingHighlightState.set(currentKey, merged.highlights);
  pendingReadingSelection = null;
  window.getSelection()?.removeAllRanges();
  renderReadingHighlights(container, currentKey);
  return { container, currentKey, highlight: merged.highlight };
}

function showSelectionToolbar(anchorRect) {
  closeReadingFloatingControls();

  const toolbar = createFloatingShell('ielts-reading-selection-toolbar', anchorRect, 108);
  toolbar.style.display = 'flex';
  toolbar.style.gap = '10px';
  toolbar.style.alignItems = 'center';
  toolbar.style.justifyContent = 'center';
  toolbar.style.padding = '8px';

  const markerButton = createToolbarIconButton(getMarkerIconSvg(), 'Highlight selected text', () => {
    saveReadingSelection('marker');
    closeReadingFloatingControls();
  });

  const noteButton = createToolbarIconButton(getNoteIconSvg(), 'Add note to selected text', () => {
    const saved = saveReadingSelection('note');
    closeReadingFloatingControls();
    if (!saved) return;
    const mark = saved.container.querySelector(`[data-highlight-id="${saved.highlight.id}"]`);
    if (mark) showReadingNotePopover(mark, saved.currentKey, saved.highlight.id);
  });

  toolbar.append(markerButton, noteButton);
  document.body.appendChild(toolbar);
}

function showReadingNotePopover(anchor, key, highlightId) {
  const highlights = readingHighlightState.get(key) || [];
  const highlight = highlights.find((item) => item.id === highlightId);
  if (!highlight) return;

  closeReadingFloatingControls();

  const popover = createFloatingShell('ielts-reading-note-popover', anchor.getBoundingClientRect(), 280);

  const textarea = document.createElement('textarea');
  textarea.value = highlight.note || '';
  textarea.placeholder = 'Write a note for this highlight';
  textarea.style.width = '100%';
  textarea.style.minHeight = '82px';
  textarea.style.resize = 'vertical';
  textarea.style.boxSizing = 'border-box';
  textarea.style.border = '1px solid #c9b66a';
  textarea.style.borderRadius = '6px';
  textarea.style.padding = '8px';
  textarea.style.fontSize = '14px';
  textarea.style.marginBottom = '10px';

  const actions = document.createElement('div');
  actions.style.display = 'flex';
  actions.style.gap = '8px';
  actions.style.justifyContent = 'flex-end';

  const saveButton = createControlButton('Save', 'Save note', 'primary');
  const deleteButton = createControlButton('Delete note', 'Delete note and highlight', 'danger');
  const cancelButton = createControlButton('Cancel', 'Close note editor');

  saveButton.addEventListener('click', () => {
    highlight.note = textarea.value.trim();
    highlight.type = 'note';
    readingHighlightState.set(key, highlights);
    closeReadingFloatingControls();
    const container = getReadingPassageContainer();
    if (container) renderReadingHighlights(container, key);
  });

  deleteButton.addEventListener('click', () => {
    readingHighlightState.set(key, highlights.filter((item) => item.id !== highlightId));
    closeReadingFloatingControls();
    const container = getReadingPassageContainer();
    if (container) renderReadingHighlights(container, key);
  });

  cancelButton.addEventListener('click', closeReadingFloatingControls);

  actions.append(cancelButton, deleteButton, saveButton);
  popover.append(textarea, actions);
  document.body.appendChild(popover);
  textarea.focus();
}

function showMarkerOptionsPopover(anchor, key, highlightId) {
  const highlights = readingHighlightState.get(key) || [];
  const highlight = highlights.find((item) => item.id === highlightId);
  if (!highlight) return;

  closeReadingFloatingControls();

  const popover = createFloatingShell('ielts-reading-note-popover', anchor.getBoundingClientRect(), 230);
  const text = document.createElement('div');
  text.textContent = 'Highlighted text';
  text.style.fontSize = '14px';
  text.style.fontWeight = '600';
  text.style.marginBottom = '10px';
  text.style.color = '#333';

  const actions = document.createElement('div');
  actions.style.display = 'flex';
  actions.style.gap = '8px';
  actions.style.justifyContent = 'flex-end';

  const removeButton = createControlButton('Remove highlight', 'Remove yellow highlight', 'danger');
  const cancelButton = createControlButton('Cancel', 'Close');

  removeButton.addEventListener('click', () => {
    readingHighlightState.set(key, highlights.filter((item) => item.id !== highlightId));
    closeReadingFloatingControls();
    const container = getReadingPassageContainer();
    if (container) renderReadingHighlights(container, key);
  });

  cancelButton.addEventListener('click', closeReadingFloatingControls);
  actions.append(cancelButton, removeButton);
  popover.append(text, actions);
  document.body.appendChild(popover);
}

function ensureReadingPassageNotes() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  window.setTimeout(() => {
    const container = getReadingPassageContainer();
    if (!container) return;

    const key = getReadingHighlightKey(container);
    renderReadingHighlights(container, key);

    if (readingHighlightController) return;

    readingHighlightController = new AbortController();
    const { signal } = readingHighlightController;

    document.addEventListener('mouseup', () => {
      const currentContainer = getReadingPassageContainer();
      const selection = window.getSelection();
      if (!currentContainer || !selection || selection.isCollapsed || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (!currentContainer.contains(range.commonAncestorContainer)) return;

      const text = selection.toString().trim();
      if (!text) return;

      const offsets = getRangeOffsets(currentContainer, range);
      if (!offsets) return;

      pendingReadingSelection = { ...offsets, text };
      suppressReadingDocumentClicksUntil = Date.now() + 600;
      showSelectionToolbar(range.getBoundingClientRect());
    }, { signal });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (target?.closest?.('#ielts-reading-selection-toolbar') || target?.closest?.('#ielts-reading-note-popover')) {
        return;
      }

      if (Date.now() < suppressReadingDocumentClicksUntil) {
        return;
      }

      if (target?.classList?.contains(READING_HIGHLIGHT_CLASS)) {
        const currentContainer = getReadingPassageContainer();
        if (!currentContainer) return;
        const currentKey = getReadingHighlightKey(currentContainer);
        const highlights = readingHighlightState.get(currentKey) || [];
        const highlight = highlights.find((item) => item.id === target.dataset.highlightId);
        if (highlight?.type === 'note') {
          showReadingNotePopover(target, currentKey, target.dataset.highlightId);
        } else {
          showMarkerOptionsPopover(target, currentKey, target.dataset.highlightId);
        }
        return;
      }

      closeReadingFloatingControls();
      pendingReadingSelection = null;
    }, { signal });
  }, 0);
}


const OFFICIAL_ANSWER_HIGHLIGHT_CLASS = 'ielts-official-answer-highlight';
const OFFICIAL_ANSWER_HIGHLIGHT_ACTIVE_CLASS = 'ielts-official-answer-highlight-active';

function normalizeOfficialHighlightRange(rawRange) {
  const cleaned = String(rawRange || '')
    .trim()
    .replace(/^questions?\s*/i, '')
    .replace(/^q\s*/i, '')
    .replace(/\s+/g, '');
  const match = cleaned.match(/^(\d+)(?:-(\d+))?$/);
  if (!match) return { questionRange: '', questionNumbers: [] };
  const start = parseInt(match[1], 10);
  const end = match[2] ? parseInt(match[2], 10) : start;
  if (!Number.isInteger(start) || !Number.isInteger(end) || start <= 0 || end < start) {
    return { questionRange: '', questionNumbers: [] };
  }
  const questionNumbers = [];
  for (let num = start; num <= end; num += 1) questionNumbers.push(String(num));
  return {
    questionRange: start === end ? String(start) : `${start}-${end}`,
    questionNumbers,
  };
}

export function normalizeOfficialAnswerHighlights(input) {
  let highlights = input;
  if (typeof highlights === 'string') {
    try {
      highlights = JSON.parse(highlights);
    } catch (error) {
      highlights = [];
    }
  }
  if (!Array.isArray(highlights)) return [];

  return highlights
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const parsedRange = normalizeOfficialHighlightRange(item.questionRange || item.question_range || item.question || item.label);
      if (parsedRange.questionNumbers.length === 0) return null;
      const start = Number(item.start);
      const end = Number(item.end);
      const selectedText = String(item.selectedText || item.selected_text || item.text || '').trim();
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !selectedText) return null;
      return {
        id: String(item.id || `${parsedRange.questionRange}-${start}-${end}-${index}`),
        questionRange: parsedRange.questionRange,
        questionNumbers: parsedRange.questionNumbers,
        start: Math.max(0, Math.floor(start)),
        end: Math.max(0, Math.floor(end)),
        selectedText,
        explanation: String(item.explanation || item.explanationHtml || item.explanation_html || item.note || '').trim(),
      };
    })
    .filter(Boolean);
}


function escapeOfficialExplanationText(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function getPlainOfficialExplanation(html) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = sanitizeOfficialExplanationHtml(html || '');
  return wrapper.textContent?.trim() || 'No explanation available.';
}

export function sanitizeOfficialExplanationHtml(html) {
  const raw = String(html || '').trim();
  if (!raw) return '<p>No explanation available.</p>';

  const source = /<[^>]+>/.test(raw) ? raw : escapeOfficialExplanationText(raw);
  const template = document.createElement('template');
  template.innerHTML = source;

  const allowedTags = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'A']);
  const allowedAttrs = new Set(['href', 'target', 'rel']);
  const nodes = template.content.querySelectorAll('*');

  nodes.forEach((node) => {
    if (!allowedTags.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }

    Array.from(node.attributes).forEach((attr) => {
      const attrName = attr.name.toLowerCase();
      if (!allowedAttrs.has(attrName)) {
        node.removeAttribute(attr.name);
        return;
      }

      if (node.tagName !== 'A') {
        node.removeAttribute(attr.name);
        return;
      }

      if (attrName === 'href' && !/^https?:\/\//i.test(attr.value)) {
        node.removeAttribute('href');
      }
    });

    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return template.innerHTML || '<p>No explanation available.</p>';
}


function closeOfficialAnswerTooltip() {
  const existing = document.getElementById('ielts-official-answer-tooltip');
  if (existing) existing.remove();
}

function showOfficialAnswerTooltip(anchor, explanationHtml) {
  closeOfficialAnswerTooltip();

  const tooltip = document.createElement('div');
  tooltip.id = 'ielts-official-answer-tooltip';
  tooltip.innerHTML = sanitizeOfficialExplanationHtml(explanationHtml);
  tooltip.style.position = 'fixed';
  tooltip.style.zIndex = '10000';
  tooltip.style.maxWidth = '360px';
  tooltip.style.padding = '10px 12px';
  tooltip.style.borderRadius = '8px';
  tooltip.style.background = '#2f2358';
  tooltip.style.color = '#fff';
  tooltip.style.fontSize = '14px';
  tooltip.style.lineHeight = '1.45';
  tooltip.style.boxShadow = '0 10px 30px rgba(0,0,0,0.18)';

  const rect = anchor.getBoundingClientRect();
  const width = 360;
  const left = Math.min(rect.left, window.innerWidth - width - 12);
  const top = rect.bottom + 8 > window.innerHeight - 120 ? rect.top - 88 : rect.bottom + 8;
  tooltip.style.left = `${Math.max(12, left)}px`;
  tooltip.style.top = `${Math.max(12, top)}px`;

  tooltip.addEventListener('click', (event) => event.stopPropagation());
  document.body.appendChild(tooltip);

  window.setTimeout(() => {
    const close = (event) => {
      if (!tooltip.contains(event.target)) {
        closeOfficialAnswerTooltip();
        document.removeEventListener('click', close);
      }
    };
    document.addEventListener('click', close);
  }, 0);
}

function unwrapOfficialAnswerHighlights(container) {
  container.querySelectorAll(`.${OFFICIAL_ANSWER_HIGHLIGHT_CLASS}`).forEach((highlight) => {
    highlight.replaceWith(document.createTextNode(highlight.dataset.originalText || ''));
  });
  container.normalize();
}

export function resetReadingPassageHighlights(options = {}) {
  const { includeUser = true, includeOfficial = true } = options;

  if (includeUser) readingHighlightState.clear();

  document.querySelectorAll('.ielts-passage-html').forEach((container) => {
    if (includeOfficial) unwrapOfficialAnswerHighlights(container);
    if (includeUser) unwrapExistingHighlights(container);
  });
}

function findOfficialHighlightOffsets(container, highlight) {
  const text = getContainerText(container);
  const directText = text.slice(highlight.start, highlight.end);
  if (directText && directText.replace(/\s+/g, ' ').trim() === highlight.selectedText.replace(/\s+/g, ' ').trim()) {
    return { start: highlight.start, end: highlight.end };
  }

  const exactIndex = text.indexOf(highlight.selectedText);
  if (exactIndex >= 0) {
    return { start: exactIndex, end: exactIndex + highlight.selectedText.length };
  }

  return { start: highlight.start, end: highlight.end };
}

export function renderOfficialAnswerHighlights(container, inputHighlights) {
  if (!container) return;
  const highlights = normalizeOfficialAnswerHighlights(inputHighlights);
  const readingKey = getReadingHighlightKey(container);
  const storedReadingHighlights = readingHighlightState.get(readingKey) || [];
  const shouldRestoreReadingHighlights = storedReadingHighlights.length > 0;
  const officialRanges = [];
  unwrapOfficialAnswerHighlights(container);
  unwrapExistingHighlights(container);
  if (highlights.length === 0) {
    if (shouldRestoreReadingHighlights) renderReadingHighlights(container, readingKey);
    return;
  }

  [...highlights]
    .sort((a, b) => b.start - a.start)
    .forEach((highlight) => {
      const offsets = findOfficialHighlightOffsets(container, highlight);
      officialRanges.push(offsets);
      const explanationText = getPlainOfficialExplanation(highlight.explanation);
      const officialSegments = splitRangeByOverlap(offsets, storedReadingHighlights);

      officialSegments
        .sort((a, b) => b.start - a.start)
        .forEach((segment) => {
          const nodes = getTextNodes(container);
          const startPoint = findNodeAtOffset(nodes, segment.start, 'start');
          const endPoint = findNodeAtOffset(nodes, segment.end, 'end');
          if (!startPoint || !endPoint) return;

          const range = document.createRange();
          try {
            range.setStart(startPoint.node, startPoint.offset);
            range.setEnd(endPoint.node, endPoint.offset);
          } catch (error) {
            return;
          }

          const originalText = range.toString();
          if (!originalText.trim()) return;

          const mark = document.createElement('mark');
          mark.className = OFFICIAL_ANSWER_HIGHLIGHT_CLASS;
          if (segment.userOverlap) {
            mark.classList.add('ielts-official-answer-highlight-user-overlap');
          }
          mark.dataset.highlightId = highlight.id;
          mark.dataset.questionRange = highlight.questionRange;
          mark.dataset.questionNumbers = highlight.questionNumbers.join(',');
          mark.dataset.originalText = originalText;
          mark.title = highlight.explanation ? explanationText : `Answer location for Q${highlight.questionRange}`;

          const isFinalSegment = segment.end === offsets.end;
          let label = null;
          let info = null;
          if (isFinalSegment) {
            label = document.createElement('span');
            label.className = 'ielts-official-answer-label';
            label.textContent = `Q${highlight.questionRange}`;
            label.setAttribute('aria-hidden', 'true');

            info = document.createElement('span');
            info.className = 'ielts-official-answer-info';
            info.textContent = 'i';
            info.title = explanationText;
            info.setAttribute('role', 'button');
            info.setAttribute('tabindex', '0');
            info.addEventListener('click', (event) => {
              event.stopPropagation();
              showOfficialAnswerTooltip(info, highlight.explanation || 'No explanation available.');
            });
            info.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                showOfficialAnswerTooltip(info, highlight.explanation || 'No explanation available.');
              }
            });
          }

          try {
            range.surroundContents(mark);
          } catch (error) {
            mark.textContent = originalText;
            range.deleteContents();
            range.insertNode(mark);
          }
          if (label && info) {
            mark.appendChild(label);
            mark.appendChild(info);
          }
        }
      );
    });

  if (shouldRestoreReadingHighlights) {
    renderReadingHighlights(container, readingKey, getVisibleReadingHighlights(container, readingKey, officialRanges));
  }
}

export function scrollToOfficialAnswerHighlight(questionNumber) {
  const q = String(questionNumber || '').trim();
  if (!q) return false;
  const highlights = document.querySelectorAll(`.${OFFICIAL_ANSWER_HIGHLIGHT_CLASS}`);
  for (const highlight of highlights) {
    const numbers = (highlight.dataset.questionNumbers || '').split(',').map(item => item.trim());
    if (numbers.includes(q)) {
      document.querySelectorAll(`.${OFFICIAL_ANSWER_HIGHLIGHT_ACTIVE_CLASS}`).forEach((el) => {
        el.classList.remove(OFFICIAL_ANSWER_HIGHLIGHT_ACTIVE_CLASS);
      });
      highlight.classList.add(OFFICIAL_ANSWER_HIGHLIGHT_ACTIVE_CLASS);
      highlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => highlight.classList.remove(OFFICIAL_ANSWER_HIGHLIGHT_ACTIVE_CLASS), 1800);
      return true;
    }
  }
  return false;
}

/**
 * Extract blank-to-number mapping and clean HTML
 */
export function extractBlankNumberMappingAndCleanHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blankToNumber = {};
  const selectionToNumber = {};

  doc.querySelectorAll('p').forEach(p => {
    const pHTML = p.innerHTML;
    const markers = [];
    const blanks = [];

    const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
    let markerMatch;
    while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
      markers.push({
        number: markerMatch[2] || markerMatch[3],
        index: markerMatch.index
      });
    }

    p.querySelectorAll('.tiptap-input-field').forEach(span => {
      const inTable = span.closest('table');
      const inQuestionBoxContent = span.closest('.question-box-content');
      if (!inTable || inQuestionBoxContent) {
        const spanHTML = span.outerHTML;
        const spanIndex = pHTML.indexOf(spanHTML);
        if (spanIndex !== -1) {
          blanks.push({ span, index: spanIndex });
        }
      }
    });

    const selections = [];
    p.querySelectorAll('.selection-field').forEach(span => {
      const inTable = span.closest('table');
      const inQuestionBoxContent = span.closest('.question-box-content');
      if (!inTable || inQuestionBoxContent) {
        const spanHTML = span.outerHTML;
        const spanIndex = pHTML.indexOf(spanHTML);
        if (spanIndex !== -1) {
          selections.push({ span, index: spanIndex });
        }
      }
    });

    blanks.forEach(blank => {
      let matchedMarker = null;
      for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].index < blank.index) {
          matchedMarker = markers[i];
          break;
        }
      }

      if (matchedMarker && blank.span && blank.span.id) {
        blankToNumber[blank.span.id] = matchedMarker.number;
      }
    });

    selections.forEach(selection => {
      let matchedMarker = null;
      for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].index < selection.index) {
          matchedMarker = markers[i];
          break;
        }
      }
      if (matchedMarker && selection.span && selection.span.id) {
        selectionToNumber[selection.span.id] = matchedMarker.number;
      }
    });
  });

  doc.querySelectorAll('.question-box-content').forEach(boxContent => {
    if (boxContent.innerHTML) {
      boxContent.innerHTML = cleanAngleBrackets(boxContent.innerHTML);
    }
  });

  doc.querySelectorAll('p').forEach(p => {
    p.innerHTML = p.innerHTML.replace(/(&lt;\d+&gt;|<\d+>)/g, '');
  });

  return {
    cleanedHtml: doc.body.innerHTML,
    blankToNumber,
    selectionToNumber
  };
}

/**
 * Process passage text to replace blanks with placeholders for hydration
 */
export function processPassageText(html) {
  return html.replace(
    /<span class=\"tiptap-input-field\" id=\"(blank-[^\"]+)\">.*?<\/span>/g,
    (match, id) => `<span data-blank-id="${id}"></span>`
  );
}
